"use client";

import React from "react";
import { useUpdateTicketAssignment, useCreateTicketUpdate } from "@/lib/hooks/use-api";
import { useUniversalAuth } from "@wtc/ui";
import { AssignToUserSelect } from "@/components/shared/selects/AssignToUserSelect";

interface AssignTicketToUserSelectProps {
  ticketId: string;
  currentUserId?: string;
  onSuccess?: () => void;
  disabled?: boolean;
  placeholder?: string;
  showUnassigned?: boolean;
  showAllUsers?: boolean;
  /** When true, expand inline instead of popover */
  expandInline?: boolean;
  expandInlineVariant?: "replace" | "floating";
  /** Called after successful assign with the new user id (or null if unassigned) */
  onSuccessWithValue?: (newUserId: string | null) => void;
  className?: string;
}

export const AssignTicketToUserSelect: React.FC<AssignTicketToUserSelectProps> = ({
  ticketId,
  currentUserId,
  onSuccess,
  disabled = false,
  placeholder = "Assign to...",
  showUnassigned = true,
  showAllUsers = false,
  expandInline = false,
  expandInlineVariant = "replace",
  onSuccessWithValue,
  className,
}) => {
  const { appUser } = useUniversalAuth();

  // Mutations for ticket assignment and audit trail
  const updateTicketAssignment = useUpdateTicketAssignment();
  const createTicketUpdate = useCreateTicketUpdate();

  const onAssign = async (assignedTo: string | null, selectedKey: string) => {
    await updateTicketAssignment.mutateAsync({
      id: ticketId,
      assigned_to: assignedTo,
    });

    if (appUser?.id) {
      await createTicketUpdate.mutateAsync({
        ticket_id: ticketId,
        update_type: "assignment",
        content: `Ticket assigned to ${selectedKey}`,
        is_internal: false,
      });
    }
  };

  const getSuccessMessage = (assignedTo: string | null, selectedKey: string) => {
    return assignedTo ? `Ticket assigned to ${selectedKey}` : "Ticket unassigned";
  };

  return (
    <AssignToUserSelect
      currentUserId={currentUserId}
      onAssign={onAssign}
      onSuccess={onSuccess}
      onSuccessWithValue={onSuccessWithValue}
      disabled={disabled}
      placeholder={placeholder}
      showUnassigned={showUnassigned}
      showAllUsers={showAllUsers}
      getSuccessMessage={getSuccessMessage}
      expandInline={expandInline}
      expandInlineVariant={expandInlineVariant}
      className={className}
    />
  );
};
